// @ts-nocheck
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Box,
  Typography,
  Grid,
  InputLabel,
  Button,
  CircularProgress,
} from "@mui/material";
import addContractorModalStyles from "../addContractorModal.module.css";
import RequiredFieldsNote from "./_components/RequiredFieldsNote";
import { requiredLabelSx } from "./_components/requiredField";
import { CustomTextFiled } from "../../../../components/Custom/Input";
import { addContractorStep3Action } from "../../../../store/actions/contractor";
import { ALERT_DIALOG } from "../../../../store/constants/auth";
import {
  ADD_CONTRACTOR_STEP3_FAILURE,
  ADD_CONTRACTOR_STEP3_REQUEST,
  ADD_CONTRACTOR_STEP3_SUCCESS,
  SET_CONTRACTOR_MULTIPLE_FIELDS,
} from "../../../../store/constants/contractor";
import { isUserRole } from "../../../../utils/methods/storeFunc";
import { USER_ROLES } from "../../../../staticData/userRoles";

const AddContractorFormStep3 = ({
  setCurrentStep,
  uiController,
  FormDataBox,
  isEditing,
  isViewing,
  successCallbackStep3,
}) => {
  // useMemo start...
  const isContractorUser = useMemo(() => {
    if (
      isUserRole(
        USER_ROLES.ADMIN,
        USER_ROLES.SUPERADMIN,
        USER_ROLES.COORDINATOR,
      )
    ) {
      return false;
    } else return true;
  }, []);
  // useMemo end...

  // State variables start...
  const [initialValues, setInitialValues] = React.useState({
    careerHighlights: "",
    professionalGoals: "",
  });

  const [isValuesSyncedOnce, setIsValuesSyncedOnce] = React.useState(false);
  // State variables end...

  // Redux hooks start...
  const dispatch = useDispatch();
  // Redux hooks end...

  // Selectors start...
  const contractor = useSelector((store) => store.contractor);
  // Selectors end...

  // Form schema...
  const formSchema = Yup.object().shape({
    careerHighlights: Yup.string()
      .max(600, "This answer should be at most 600 characters!")
      .required("This answer is required!"),
    // .when("$isContractorUser", {
    //   is: true,
    //   then: (schema) => schema.required("Career highlights is required!"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    professionalGoals: Yup.string()
      // Optional since 2026-10-09 (Tiffaney); stored as `professionalGoals`.
      .max(600, "This answer should be at most 600 characters!"),
    // .when("$isContractorUser", {
    //   is: true,
    //   then: (schema) => schema.required("Professional goals is required!"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: formSchema,
    validationContext: {
      isContractorUser,
    },
    onSubmit: (values) => {
      const payload = {
        ...values,
      };

      // NOTE (2026-08-03): removed the same "skip the PUT when nothing changed"
      // short-circuit that broke step 2 (see AddContractorFormStep2). It compared
      // step3.form against step3.previouslySubmittedForm, and the prefill
      // (AddContractorModal.tsx) builds those two objects IDENTICAL while nothing
      // updates them as the user types (Formik state lives in `values`). So the
      // guard fired on every click: the step advanced, PUT /addCareerGoals never
      // went out, isCareerGoalsAdded stayed false and the profile wizard reopened
      // on the same step. The PUT is idempotent and is what sets the flag, so
      // always send it. (Step 1 keeps its guard on purpose: there the comparison
      // is `values` vs previouslySubmittedForm, which does detect real edits.)
      dispatch({ type: ADD_CONTRACTOR_STEP3_REQUEST });
      dispatch(
        addContractorStep3Action({
          payload,
          contractorId: contractor?.addContractor?.step1?.data?._id,
          formData: values,
          successCallback: ({ response }) => {
            setCurrentStep((currentStep) => {
              return currentStep + 1;
            });
            dispatch({
              type: ADD_CONTRACTOR_STEP3_SUCCESS,
              payload: {
                data: response?.data?.data,
                formData: values,
              },
            });

            if (successCallbackStep3) {
              successCallbackStep3(response);
            }
          },
          failureCallback: ({ error }) => {
            dispatch({ type: ADD_CONTRACTOR_STEP3_FAILURE });
            // Without this the step failed silently (parity with steps 1 and 2).
            dispatch({
              type: ALERT_DIALOG,
              payload: {
                open: true,
                title: "Error!",
                message:
                  error?.response?.data?.message ||
                  "Could not save your career goals. Please try again.",
              },
            });
          },
          afterResponseCallback: () => {},
        }),
      );
    },
  });

  const {
    values,
    setValues,
    handleChange,
    handleBlur,
    touched,
    errors,
    submitForm,
    resetForm,
  } = formik;
  // Form schema...

  // Handlers start...
  // Handlers end...

  // useEffects start...
  React.useEffect(() => {
    if (contractor?.addContractor?.step3?.form) {
      setValues(contractor?.addContractor?.step3?.form);
      setInitialValues(contractor?.addContractor?.step3?.form);
    }
  }, []);

  React.useEffect(() => {
    dispatch({
      type: SET_CONTRACTOR_MULTIPLE_FIELDS,
      payload: {
        addContractor: {
          ...contractor?.addContractor,
          step3: {
            ...contractor?.addContractor?.step3,
            form: contractor.addContractor?.step3?.form
              ? isValuesSyncedOnce
                ? values
                : contractor.addContractor?.step3?.form
              : values,
          },
        },
      },
    });

    setIsValuesSyncedOnce(true);
  }, [values, dispatch]);

  // reset form...
  // This reset is probably not required...
  // React.useEffect(() => {
  //     if (typeof contractor?.addContractor?.reset?.count === "number") {
  //       console.log("resetFormAddCont", contractor?.addContractor?.reset?.count)
  //       resetForm();
  //     }
  // }, [contractor?.addContractor?.reset?.count]);

  // React.useEffect(() => {
  //     if (isEditing && !contractor?.addContractor?.step3?.previouslySubmittedForm) {
  //         dispatch({
  //             type: SET_CONTRACTOR_MULTIPLE_FIELDS,
  //             payload: {
  //                 addContractor: {
  //                     ...contractor?.addContractor,
  //                     step3: {
  //                         ...contractor?.addContractor?.step3,
  //                         // form: contractor.addContractor?.step3?.form ? (
  //                         //     isValuesSyncedOnce ? values : contractor.addContractor?.step3?.form
  //                         // ) : values,
  //                         form: {
  //                             careerHighlights: 'highlights',
  //                             professionalGoals: 'goals'
  //                         },
  //                         previouslySubmittedForm: {
  //                             careerHighlights: 'highlights',
  //                             professionalGoals: 'goals'
  //                         },
  //                         data: {
  //                             "_id": "66577b3efc6cfea977b91fee",
  //                             "firstName": "firstname",
  //                             "lastName": "LastName",
  //                             "email": "firstname10@yopmail.com",
  //                             "mailingAddress": "123",
  //                             "mobile": "700-219-8426",
  //                             "state": "Alaska",
  //                             "city": "Anchorage Municipality",
  //                             "zipCode": "781034",
  //                             "einNumber": 123,
  //                             "companyName": "c",
  //                             "userRole": "contractor",
  //                             "isActive": true,
  //                             "isDeleted": false,
  //                             "isApproved": false,
  //                             "isTwoFactorAuthentication": false,
  //                             "emailNotificationPreference": {
  //                                 "isProfileLogin": false,
  //                                 "isEventAdd": false,
  //                                 "isEventUpdate": false,
  //                                 "isGenerateEstimate": false
  //                             },
  //                             "isContractorFormCompleted": false,
  //                             "roleExperience": [],
  //                             "certificatesAccreditations": "cert",
  //                             "preferredPiecesOfGear": "gear",
  //                             "rolesWorked": "role",
  //                             "careerHighlights": "highlights",
  //                             "professionalGoals": "goals",
  //                             "isPersonalInformationAdded": true,
  //                             "isRoleInformationAdded": true,
  //                             "isCareerGoalsAdded": true,
  //                             "createdAt": "2024-05-29T22:16:53.274Z",
  //                             "updatedAt": "2024-05-29T22:18:17.481Z"
  //                         }
  //                     }
  //                 }
  //             }
  //         });
  //         setValues({
  //             careerHighlights: 'highlights',
  //             professionalGoals: 'goals'
  //         });
  //     }
  // }, []);
  // useEffects end...

  return (
    <>
      {/* <Box className={addContractorModalStyles["formDataBox"]}
            > */}
      <FormDataBox>
        {/* <Grid container spacing={2}
                    sx={{
                        height: '100%',
                        overflowY: 'auto'
                    }}         
                    className={'custom-scroll-design'}               
                > */}
        {isViewing ? null : <RequiredFieldsNote />}
        <Grid item xs={12}>
          <Box className={addContractorModalStyles["form-box"]}>
            {/* Red until it is answered. */}
            <InputLabel
              sx={isViewing || String(values?.careerHighlights ?? "").trim() ? undefined : requiredLabelSx}
            >
              {/* Stored as `careerHighlights`; the question changed (Tiffaney, 2026-10-09). */}
              What roles are you most comfortable performing and why?
            </InputLabel>
            <CustomTextFiled
              fullWidth
              placeholder="Enter text here..."
              name="careerHighlights"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.careerHighlights}
              multiline={true}
              rows={7}
              sx={{
                height: "fit-content !important",
              }}
              inputProps={{
                readOnly: Boolean(isViewing),
              }}
            />
            {touched.careerHighlights && errors.careerHighlights && (
              <span className={addContractorModalStyles.error}>
                {errors.careerHighlights}
              </span>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel>
              Is there anything else about your AV career you would like us to know? (Optional)
            </InputLabel>
            <CustomTextFiled
              fullWidth
              placeholder="Enter text here..."
              name="professionalGoals"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.professionalGoals}
              multiline={true}
              rows={7}
              sx={{
                height: "fit-content !important",
              }}
              inputProps={{
                readOnly: Boolean(isViewing),
              }}
            />
            {touched.professionalGoals && errors.professionalGoals && (
              <span className={addContractorModalStyles.error}>
                {errors.professionalGoals}
              </span>
            )}
          </Box>
        </Grid>
        {/* </Grid> */}
        {/* </Box> */}
      </FormDataBox>

      {uiController?.showBottomBtnBox ? (
        <Box
          className="bottom-btn-box"
          sx={{
            width: "100%",
            marginTop: "2rem",
            alignSelf: "flex-end",
          }}
        >
          <Button
            className="btn-black"
            sx={{
              width: "50%",
              height: "4.25rem",
              textTransform: "none !important",
            }}
            onClick={() => {
              // addContractorsDispatch({
              // type: 'RESET'
              // });
              // resetForm();
              setCurrentStep((currentStep) => {
                return currentStep - 1;
              });
            }}
            // onClick={onClose}
          >
            <Typography className="btn-text">Back</Typography>
          </Button>

          <Button
            className="btn-pink"
            sx={{
              width: "50%",
              height: "4.25rem",
              textTransform: "none !important",
              "&:disabled": {
                background: "#d85ad8 !important",
              },
            }}
            onClick={() => {
              if (isViewing) {
                setCurrentStep((currentStep) => {
                  return currentStep + 1;
                });
                return;
              }
              submitForm();
            }}
            disabled={
              contractor?.addContractor?.step3?.submitFormRequestLoading
            }
            // onClick={onClose}
          >
            {contractor?.addContractor?.step3?.submitFormRequestLoading ? (
              <CircularProgress size={22} />
            ) : (
              <Typography className="btn-text">
                {isViewing ? "Continue" : "Save and Continue"}
              </Typography>
            )}
          </Button>
        </Box>
      ) : null}
    </>
  );
};

AddContractorFormStep3.propTypes = {
  currentStep: PropTypes.number,
  setCurrentStep: PropTypes.func,
  uiController: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  isViewing: PropTypes.bool.isRequired,
  successCallbackStep3: PropTypes.func,
};

export default AddContractorFormStep3;